import AddIcon from "@mui/icons-material/AddRounded";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import objectHash from "object-hash";
import { useCallback, useMemo, useState } from "react";
import { createApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ObjectMetricExtension } from "@entrilia/shared/api/dataCollectionTypes";
import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DataCollectionSubmissionDetails } from "../../../../api/types/dataCollectionTypes";
import AddNewMetricExtensionDialog from "./AddNewMetricExtensionDialog";
import MetricExtensionManagementTable from "./MetricExtensionManagementTable";
import {
  addExtensionsAction,
  getInitialMetricExtensionManagementState,
  loadExtensionsAction,
  metricChangeAction,
  renameAction,
  searchAction,
  updateExtensionRowsAction,
  visibilityChangeAction,
} from "./metricExtensionsManagementState";

interface Props {
  onClose: (isFormUpdateRequired: boolean) => void;
  dataSubmission: DataCollectionSubmissionDetails;
}

const updateObjectMetricExtensions = withErrorHandling(adminApi.updateObjectMetricExtensions);
const renameObjectMetricExtension = withErrorHandling(adminApi.renameObjectMetricExtension);

const ManageMetricExtensionsDialog = ({ onClose, dataSubmission }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [state, setState] = useState(() => getInitialMetricExtensionManagementState(dataSubmission));
  const [saving, setSaving] = useState(false);
  const [changesMade, setChangesMade] = useState(false);
  const [addingExtension, setAddingExtension] = useState(false);

  const metricIds = useMemo(() => state.metrics.flatMap((metric) => metric.metricIds), [state.metrics]);

  const filteredRows = useMemo(() => {
    const extensionRows = state.selectedMetric?.extensionRows ?? [];
    return state.searchTerm
      ? extensionRows.filter((r) => r.extensionValue.toLowerCase().includes(state.searchTerm))
      : extensionRows;
  }, [state.selectedMetric?.extensionRows, state.searchTerm]);

  const metricIdsHash = objectHash(metricIds, { unorderedArrays: true });
  const getExtensions = useCallback(() => {
    if (metricIds.length === 0) {
      return Promise.resolve(createApiResponse([]));
    }

    return adminApi.searchObjectMetricExtensions({
      objectId: dataSubmission.recipientObjectId,
      metricIds,
      includeHidden: true,
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [dataSubmission.recipientObjectId, metricIdsHash]);

  const [, fetchError, { isFetching }] = useFetch(getExtensions, {
    onSetData: (data) => setState(loadExtensionsAction(data)),
  });

  const handleMetricChange = useCallback((id: string) => {
    setState(metricChangeAction(id));
  }, []);

  const handleVisibilityChange = useCallback((rowId: string, visible: boolean) => {
    setState(visibilityChangeAction(rowId, visible));
  }, []);

  const handleRename = useCallback((extensionId: string, newValue: string) => {
    setState(renameAction(extensionId, newValue));
  }, []);

  if (fetchError) {
    logError(fetchError, "[ManageExtensionsDialog] searchExtensions");
  }

  const updateExtensions = async () => {
    if (state.updatedRowIds.size === 0) {
      return true;
    }

    const extensionRows = state.selectedMetric?.extensionRows;
    if (!extensionRows) {
      return true;
    }

    setChangesMade(true);

    const payload = {
      objectId: dataSubmission.recipientObjectId,
      extensionUpdates: extensionRows
        .filter((row) => state.updatedRowIds.has(row.id))
        .flatMap((row) => row.extensionIds.map((extensionId) => ({ id: extensionId, hidden: row.hidden }))),
    };

    const [updatedExtensions, error] = await updateObjectMetricExtensions(payload);

    if (error) {
      logError(error, "[ManageExtensionsDialog] updateObjectMetricExtensions");
      sendNotificationError(error.message);
      return false;
    }

    setState(updateExtensionRowsAction(updatedExtensions, "updated"));
    return true;
  };

  const renameExtensions = async () => {
    if (state.renamedRowIds.size === 0) {
      return true;
    }

    const extensionRows = state.selectedMetric?.extensionRows;
    if (!extensionRows) {
      return true;
    }

    setChangesMade(true);

    const errors: ApiError[] = [];
    const updatedExtensions: ObjectMetricExtension[] = [];

    for (const rowId of state.renamedRowIds) {
      const row = extensionRows.find((r) => r.id === rowId);
      if (!row) {
        continue;
      }

      for (const extensionId of row.extensionIds) {
        const [updatedExtension, error] = await renameObjectMetricExtension({
          extensionId,
          newValue: row.extensionValue,
        });

        if (error) {
          errors.push(error);
          logError(error, "[ManageExtensionsDialog] renameObjectMetricExtension");
        } else {
          updatedExtensions.push(updatedExtension);
        }
      }
    }

    if (errors.length > 0) {
      sendNotificationError(`Failed to rename ${errors.length} of ${state.renamedRowIds.size} extensions.`);
      setState(updateExtensionRowsAction(updatedExtensions));
      return false;
    }

    setState(updateExtensionRowsAction(updatedExtensions, "renamed"));
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    const success = (await updateExtensions()) && (await renameExtensions());
    setSaving(false);
    if (success) {
      sendNotification("Changes saved successfully");
    }
  };

  const handleClose = () => {
    onClose(changesMade);
  };

  const handleExtensionsCreated = (newExtensions: ObjectMetricExtension[]) => {
    setState(addExtensionsAction(newExtensions));
    setAddingExtension(false);
    setChangesMade(true);
  };

  return (
    <Dialog open maxWidth="xl" fullWidth>
      <DialogTitle>{`Manage Extensions - ${dataSubmission.recipientName}`}</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <Divider />
      <DialogContent sx={{ overflowY: "hidden", height: "80vh" }}>
        {fetchError && <DataLoadingFailed />}

        {!fetchError && isFetching && <InlineLoader />}

        {!fetchError && !isFetching && state.metrics.length === 0 && (
          <Alert severity="info" sx={{ width: "100%" }}>
            Company-specific extended metrics are not defined for this form.
          </Alert>
        )}

        {!fetchError && !isFetching && state.metrics.length > 0 && (
          <Stack spacing={2} sx={{ pt: 1, height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <FormControl>
                  <InputLabel id="metric-select-label">Metric</InputLabel>
                  <Select
                    value={state.selectedMetric?.id ?? ""}
                    labelId="metric-select-label"
                    label="Metric"
                    disabled={state.metrics.length === 1}
                    onChange={(e) => handleMetricChange(e.target.value)}
                    sx={{ width: "18rem" }}
                  >
                    {state.metrics.map((metric) => (
                      <MenuItem key={metric.id} value={metric.id}>
                        {metric.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <RecordCounter
                  totalRecords={state.selectedMetric?.extensionRows.length}
                  records={filteredRows.length}
                  hide={!state.selectedMetric}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <SearchField
                  onSearch={(value) => setState(searchAction(value))}
                  disabled={!state.selectedMetric?.extensionRows.length}
                  debounceTimeMs={300}
                />
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setAddingExtension(true)}
                  disabled={!state.selectedMetric}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Stack>
            </Box>

            {state.selectedMetric && (
              <MetricExtensionManagementTable
                rows={filteredRows}
                onVisibilityChange={handleVisibilityChange}
                onRename={handleRename}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={handleClose}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={state.updatedRowIds.size + state.renamedRowIds.size === 0}
          loading={saving}
        >
          Save
        </Button>
      </DialogButtons>
      {addingExtension && state.selectedMetric && (
        <AddNewMetricExtensionDialog
          recipientObjectId={dataSubmission.recipientObjectId}
          extendedMetric={state.selectedMetric}
          existingExtensionValues={state.selectedMetric.extensionRows.map((r) => r.extensionValue)}
          onClose={() => setAddingExtension(false)}
          onCreated={handleExtensionsCreated}
        />
      )}
    </Dialog>
  );
};

export default ManageMetricExtensionsDialog;
